import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ProcessorDTO } from '../dto/processor'
import { useProcessors } from '../hooks/useProcessor'
import { NONE_MERCHANT, useMerchantContext } from './merchant-context'
import { postProcessorField } from '../utils/processor-utils'

export const NONE_PROCESSOR = { id: 0, name: 'None' }

const PROCESSOR_PARAM_NAME = 'processor'

const addNoneToList = processors => {
  return [NONE_PROCESSOR, ...processors]
}

interface IProcessorContext {
  processorList?: ProcessorDTO[]
  isProcessorsLoading?: boolean
  isAddingProcessorField?: boolean
  newProcessorId?: number
  displayProcessorList?: ProcessorDTO[]
  selectedProcessor?: ProcessorDTO
  setSelectedProcessor?: (processorDTO: ProcessorDTO) => void
  createProcessorField?: (fieldName: string, fieldType: string) => Promise<void>
}

const ProcessorContext = createContext<IProcessorContext>({})

export const ProcessorProvider = ({ children }) => {
  const navigate = useNavigate()
  const { processorsData, isProcessorsLoading } = useProcessors()
  const { selectedMerchant } = useMerchantContext()
  const [displayProcessorList, setDisplayProcessorList] = useState<
    ProcessorDTO[]
  >([])
  const [selectedProcessor, setSelectedProcessor] = useState<ProcessorDTO>()
  const [isAddingProcessorField, setIsAddingProcessorField] = useState(false)
  const [newProcessorId, setNewProcessorId] = useState<number>(null)

  const { search, pathname } = useLocation()
  const searchParams = new URLSearchParams(search)
  const processorParamId = searchParams.get(PROCESSOR_PARAM_NAME)

  const selectProcessor = (processor: ProcessorDTO) => {
    const searchParams = new URLSearchParams(search)
    searchParams.set(PROCESSOR_PARAM_NAME, `${processor.id}`)
    navigate({ pathname, search: searchParams.toString() })
    setSelectedProcessor(processor)
  }

  useEffect(() => {
    if (!processorsData) {
      return
    }

    if (selectedMerchant?.name === NONE_MERCHANT.name) {
      setDisplayProcessorList(addNoneToList(processorsData))
    } else if (selectedMerchant) {
      setDisplayProcessorList(addNoneToList(selectedMerchant.processors || []))
    }
  }, [selectedMerchant, processorsData])

  useEffect(() => {
    if (!processorParamId || displayProcessorList?.length === 0) {
      return
    }

    const processorToSet = displayProcessorList.find(
      processor => processor.id === +processorParamId
    )

    if (!processorToSet) {
      return
    }

    selectProcessor(processorToSet)
  }, [displayProcessorList, processorParamId])

  useEffect(() => {
    if (
      !selectedMerchant ||
      !selectedProcessor ||
      selectedMerchant.name === NONE_MERCHANT.name
    ) {
      return
    }

    const merchantHasProcessor = selectedMerchant.processors?.find(
      processor => processor.id === selectedProcessor.id
    )

    if (!merchantHasProcessor) {
      selectProcessor(NONE_PROCESSOR)
    }
  }, [selectedMerchant, selectedProcessor, selectProcessor])

  const createProcessorField = async (fieldName: string, fieldType: string) => {
    setIsAddingProcessorField(true)
    try {
      const { data } = await postProcessorField(
        fieldName,
        fieldType,
        selectedProcessor.id
      )
      setNewProcessorId(data?.templateId)
    } catch (e) {
      console.error(e)
    }
    setIsAddingProcessorField(false)
  }

  const contextValue = useMemo(
    () => ({
      processorList: processorsData,
      isProcessorsLoading,
      newProcessorId,
      isAddingProcessorField,
      displayProcessorList,
      selectedProcessor,
      setSelectedProcessor: selectProcessor,
      createProcessorField
    }),
    [
      processorsData,
      isProcessorsLoading,
      newProcessorId,
      isAddingProcessorField,
      displayProcessorList,
      selectedProcessor,
      selectProcessor,
      createProcessorField
    ]
  )

  return (
    <ProcessorContext.Provider value={contextValue}>
      {children}
    </ProcessorContext.Provider>
  )
}

export const useProcessorContext = () => {
  return useContext(ProcessorContext)
}
